import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react'

import { readStorage, writeStorage } from './assistant-storage'
import { useViewportSize } from './useViewportSize'

export const DOCK_MIN_WIDTH_PX = 320
/** The dock's old fixed width, `max-w-md`. */
export const DOCK_DEFAULT_WIDTH_PX = 448
export const DOCK_KEYBOARD_STEP_PX = 16
export const DOCK_WIDTH_KEY = 'gisify.assistant.dockWidth'

/** Published on `<html>` while docked; the toast stack's `right` (≥ md) adds it. */
export const DOCK_INSET_VAR = '--assistant-dock-inset'

/** The dock is on the right, so ← widens it and → narrows it. */
const KEY_STEPS: Record<string, number> = { ArrowLeft: DOCK_KEYBOARD_STEP_PX, ArrowRight: -DOCK_KEYBOARD_STEP_PX }

/** Never more than half the window — the page always keeps at least as much room as the dock. */
export function dockMaxWidth(viewportWidth: number): number {
  return Math.max(DOCK_MIN_WIDTH_PX, Math.floor(viewportWidth / 2))
}

export function clampDockWidth(width: number, viewportWidth: number): number {
  return Math.min(Math.max(Math.round(width), DOCK_MIN_WIDTH_PX), dockMaxWidth(viewportWidth))
}

/** A stored width, read defensively: anything but a positive finite number is "nothing stored". */
export function parseDockWidth(raw: string | null): number | null {
  if (raw === null) return null
  const value = Number(raw)

  return Number.isFinite(value) && value > 0 ? value : null
}

export interface DockWidth {
  width: number
  min: number
  max: number
  resizing: boolean
  onResizeStart: (event: ReactPointerEvent<HTMLElement>) => void
  onResizeKey: (event: ReactKeyboardEvent<HTMLElement>) => void
}

/**
 * The docked panel's width: dragged from its left edge, or stepped with ← / → on the separator.
 *
 * What is remembered is the user's PREFERRED width; what renders is that preference clamped to the
 * current window. Narrowing the window shrinks the dock; widening it again gives back the width the
 * user chose, not whatever the narrow window forced.
 */
export function useDockWidth(): DockWidth {
  const viewport = useViewportSize()
  const [preferred, setPreferred] = useState(() => parseDockWidth(readStorage(DOCK_WIDTH_KEY)) ?? DOCK_DEFAULT_WIDTH_PX)
  const [resizing, setResizing] = useState(false)
  const teardownRef = useRef<(() => void) | null>(null)

  // A resize still live at unmount must not leave listeners behind, or the page's cursor stuck.
  useEffect(() => () => teardownRef.current?.(), [])

  const width = clampDockWidth(preferred, viewport.width)

  function onResizeStart(event: ReactPointerEvent<HTMLElement>) {
    // A secondary button (right-click to inspect) is not a resize.
    if (event.button > 0) return

    // Stops the press from starting a text selection across the page.
    event.preventDefault()
    // A press while a resize is still live (a release that never arrived, or a second finger)
    // replaces it: its listeners go, and the body styles it saved are put back before this press
    // saves them again. Otherwise this press would save `col-resize` as the cursor to restore.
    teardownRef.current?.()

    // Only the pointer that pressed drives the resize; another finger on the screen does not.
    const { pointerId } = event
    const startX = event.clientX
    const startWidth = width
    const startPreferred = preferred
    let last = startWidth

    try {
      event.currentTarget.setPointerCapture(pointerId)
    } catch {
      // No capture available: the window listeners still follow the gesture.
    }

    // The pointer leaves the 6px handle at once. Without this the cursor flickers back to an arrow
    // and the drag selects text in the page beneath.
    const { style } = document.body
    const previous = { cursor: style.cursor, userSelect: style.userSelect }
    style.cursor = 'col-resize'
    style.userSelect = 'none'
    setResizing(true)

    const onMove = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return

      last = clampDockWidth(startWidth + (startX - e.clientX), window.innerWidth)
      setPreferred(last)
    }

    const onUp = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return

      teardownRef.current?.()
      setResizing(false)

      // A press that changed nothing (a click on the handle, or a drag against the clamp) keeps
      // the preference. Storing the clamped width would lose the width the user chose: the next
      // time the window is wide enough, the dock would come back narrower.
      if (last === startWidth) {
        setPreferred(startPreferred)

        return
      }

      writeStorage(DOCK_WIDTH_KEY, String(last))
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    teardownRef.current = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      style.cursor = previous.cursor
      style.userSelect = previous.userSelect
      teardownRef.current = null
    }
  }

  function onResizeKey(event: ReactKeyboardEvent<HTMLElement>) {
    const step = KEY_STEPS[event.key]
    if (!step) return

    // An arrow on the focused separator never scrolls the page, even when the dock cannot move.
    event.preventDefault()
    // Steps from the width on screen, not the stored preference: while the window clamps the dock,
    // a step from the preference would land back on the clamp and change nothing.
    const next = clampDockWidth(width + step, viewport.width)
    // Already at the limit: nothing changes, and the preference is kept (see `onUp` above).
    if (next === width) return

    setPreferred(next)
    writeStorage(DOCK_WIDTH_KEY, String(next))
  }

  return { width, min: DOCK_MIN_WIDTH_PX, max: dockMaxWidth(viewport.width), resizing, onResizeStart, onResizeKey }
}

/**
 * While the dock is open, publish how much of the window's right edge it takes, for overlays pinned
 * to that edge (the toast stack) — otherwise they paint over the dock's own header. `null`
 * publishes nothing; the property is withdrawn when the dock closes.
 */
export function useDockInset(width: number | null): void {
  useLayoutEffect(() => {
    if (width === null) return undefined

    const root = document.documentElement
    root.style.setProperty(DOCK_INSET_VAR, `${width}px`)

    return () => {
      root.style.removeProperty(DOCK_INSET_VAR)
    }
  }, [width])
}
